import { useState, useEffect } from "react";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import { FaCheckCircle } from "react-icons/fa";
import "../../styles/EpisodeScheduleList.scss";

const EpisodeScheduleList = ({ episodes, onEpisodeUpdate }) => {

    // Helper function to add weeks to a date
    const addWeeks = (date, weeks) => {
        const result = new Date(date);
        result.setDate(result.getDate() + (weeks * 7));
        return result;
    };

    const handleDateChange = (episodeId, field, value) => {
        onEpisodeUpdate(episodeId, field, value);
    };

    const handleComponentChange = (episodeId, baseField, component, value, episode) => {
        // Get current datetime or use defaults
        const currentValue = episode[baseField];
        const now = new Date();

        let day, month, year, hour, minute;

        if (currentValue) {
            const dt = new Date(currentValue);
            day = dt.getDate();
            month = dt.getMonth() + 1; // 1-12
            year = dt.getFullYear();
            hour = dt.getHours();
            minute = dt.getMinutes();
        } else {
            // Defaults
            day = now.getDate();
            month = now.getMonth() + 1;
            year = now.getFullYear();
            hour = 8;
            minute = 0;
        }

        // Update the specific component
        switch(component) {
            case 'day':
                day = parseInt(value) || 1;
                break;
            case 'month':
                month = parseInt(value) || 1;
                break;
            case 'year':
                year = parseInt(value) || now.getFullYear();
                break;
            case 'hour':
                hour = parseInt(value) || 0;
                break;
            case 'minute':
                minute = parseInt(value) || 0;
                break;
        }

        // Create ISO datetime string
        const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        const combined = `${dateStr}T${timeStr}`;

        onEpisodeUpdate(episodeId, baseField, combined);
    };

    const getDateComponent = (dateString, component) => {
        if (!dateString) {
            const now = new Date();
            switch(component) {
                case 'day': return now.getDate();
                case 'month': return now.getMonth() + 1;
                case 'year': return now.getFullYear();
                case 'hour': return 8;
                case 'minute': return 0;
                default: return '';
            }
        }

        const date = new Date(dateString);
        switch(component) {
            case 'day': return date.getDate();
            case 'month': return date.getMonth() + 1;
            case 'year': return date.getFullYear();
            case 'hour': return date.getHours();
            case 'minute': return date.getMinutes();
            default: return '';
        }
    };

    const hasValidDates = (episode) => {
        if (!episode.start_date_time) {
            return false;
        }

        const startDate = new Date(episode.start_date_time);

        // Check if date is valid and not NaN
        return !isNaN(startDate.getTime());
    };

    return (
        <div>
            {episodes.map((episode, index) => {
                return (
                    <Card
                        key={episode.id}
                        className="mb-3"
                        style={{position: 'relative', borderLeft: '10px solid '+ episode.pivot.color }}
                    >
                        {hasValidDates(episode) && (
                            <FaCheckCircle
                                style={{
                                    position: 'absolute',
                                    top: '10px',
                                    right: '10px',
                                    color: '#28a745',
                                    fontSize: '20px',
                                    zIndex: 1
                                }}
                            />
                        )}
                        <Card.Body>
                            <Row>
                                <Col sm={12}>
                                    <h5 className="mb-3">{episode.title}</h5>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12} className="mb-3">
                                    <Form.Label className="fw-bold mb-2">Release Date & Time <span className="text-muted fw-normal">— {index === 0 ? "This is your club launch. Listeners get access to the club on this date, and the weekly rhythm runs from here." : "The exact moment this episode drops into the club. Listeners get it at this time in their own timezone. Default is 7am Monday; change either if there's a better window for your audience."}</span></Form.Label>
                                    <Row className="g-2">
                                        <Col xs={3}>
                                            <Form.Group>
                                                <Form.Label className="small text-muted">Day</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    min="1"
                                                    max="31"
                                                    placeholder="DD"
                                                    value={getDateComponent(episode.start_date_time, 'day')}
                                                    onChange={(e) => handleComponentChange(episode.id, 'start_date_time', 'day', e.target.value, episode)}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col xs={3}>
                                            <Form.Group>
                                                <Form.Label className="small text-muted">Month</Form.Label>
                                                <Form.Control
                                                    as="select"
                                                    value={getDateComponent(episode.start_date_time, 'month')}
                                                    onChange={(e) => handleComponentChange(episode.id, 'start_date_time', 'month', e.target.value, episode)}
                                                >
                                                    <option value="1">January</option>
                                                    <option value="2">February</option>
                                                    <option value="3">March</option>
                                                    <option value="4">April</option>
                                                    <option value="5">May</option>
                                                    <option value="6">June</option>
                                                    <option value="7">July</option>
                                                    <option value="8">August</option>
                                                    <option value="9">September</option>
                                                    <option value="10">October</option>
                                                    <option value="11">November</option>
                                                    <option value="12">December</option>
                                                </Form.Control>
                                            </Form.Group>
                                        </Col>
                                        <Col xs={3}>
                                            <Form.Group>
                                                <Form.Label className="small text-muted">Year</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    min="2020"
                                                    max="2100"
                                                    placeholder="YYYY"
                                                    value={getDateComponent(episode.start_date_time, 'year')}
                                                    onChange={(e) => handleComponentChange(episode.id, 'start_date_time', 'year', e.target.value, episode)}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col xs={3}>
                                            <Form.Group>
                                                <Form.Label className="small text-muted">Time</Form.Label>
                                                <div className="d-flex gap-1">
                                                    <Form.Control
                                                        type="number"
                                                        min="0"
                                                        max="23"
                                                        placeholder="HH"
                                                        value={getDateComponent(episode.start_date_time, 'hour')}
                                                        onChange={(e) => handleComponentChange(episode.id, 'start_date_time', 'hour', e.target.value, episode)}
                                                        style={{width: '50%'}}
                                                    />
                                                    <Form.Control
                                                        type="number"
                                                        min="0"
                                                        max="59"
                                                        placeholder="MM"
                                                        value={getDateComponent(episode.start_date_time, 'minute')}
                                                        onChange={(e) => handleComponentChange(episode.id, 'start_date_time', 'minute', e.target.value, episode)}
                                                        style={{width: '50%'}}
                                                    />
                                                </div>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                );
            })}
        </div>
    );
};

export default EpisodeScheduleList;